/**
 * The roles Event Hub renders for — the caller's own, or, while an admin is acting as someone,
 * that user's as the service reports them. See utils/effectiveRoles for why the two differ.
 *
 * EventHubLayout fetches once per impersonated login and provides the answer to every page, so
 * pages do not each make the round trip and cannot disagree with one another. Outside the
 * provider (a page rendered on its own, as in its tests) the hook falls back to the caller's own
 * roles, which is what the provider answers whenever nobody is being impersonated.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { eventHubCalendarClient } from '../client/EventCalendarClient';
import { rolesFromService, rolesOf, type EventHubRole } from '../utils/authorization';
import {
  effectiveRolesFor,
  type EventHubRolesState,
  type ServedRoles,
} from '../utils/effectiveRoles';
import { useEventHubLogin } from './useEventHubLogin';

const EventHubRolesContext = createContext<EventHubRolesState | null>(null);

export function EventHubRolesProvider({ children }: { children: ReactNode }) {
  const { ldap_groups } = useAuth();
  const { login, impersonating } = useEventHubLogin();
  const own = useMemo(() => rolesOf(ldap_groups), [ldap_groups]);
  const [served, setServed] = useState<ServedRoles | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (!impersonating) return;
    const controller = new AbortController();
    let cancelled = false;
    eventHubCalendarClient()
      .getCurrentUserRoles({ signal: controller.signal })
      .then((names) => {
        if (!cancelled) setServed({ login, roles: rolesFromService(names) });
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [impersonating, login]);

  const value = useMemo(
    () => effectiveRolesFor({ own, login, impersonating, served, error }),
    [own, login, impersonating, served, error],
  );
  return <EventHubRolesContext.Provider value={value}>{children}</EventHubRolesContext.Provider>;
}

/** The full state — the layout needs loading and error; pages need only {@link useEventHubRoles}. */
export function useEventHubRolesState(): EventHubRolesState {
  const provided = useContext(EventHubRolesContext);
  const { ldap_groups } = useAuth();
  const own = useMemo(() => rolesOf(ldap_groups), [ldap_groups]);
  return provided ?? { roles: own, loading: false, error: null };
}

/** The roles to render for. Branch on the capability predicates in utils/authorization. */
export function useEventHubRoles(): ReadonlySet<EventHubRole> {
  return useEventHubRolesState().roles;
}
